import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

function ReporteUnidades() {
  const [unidades, setUnidades] = useState([]);
  const [unidadSeleccionada, setUnidadSeleccionada] = useState('');
  const [mesSeleccionado, setMesSeleccionado] = useState('');
  const [añoSeleccionado, setAñoSeleccionado] = useState(new Date().getFullYear());
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');
  const [estadisticas, setEstadisticas] = useState({});
  const [estadisticasTodasUnidades, setEstadisticasTodasUnidades] = useState([]);
  const [resumenPorOperadora, setResumenPorOperadora] = useState({});
  const [totalViajesAutomaticos, setTotalViajesAutomaticos] = useState(0);
  const [totalCarrerasMes, setTotalCarrerasMes] = useState(0); // Total completo incluyendo sin unidad
  const [totalCarrerasNoAtendidos, setTotalCarrerasNoAtendidos] = useState(0); // Total de carreras con estado "No Atendido"
  const [totalCarrerasSinOperador, setTotalCarrerasSinOperador] = useState(0); // Total de carreras sin operador
  const [totalUbicacionesRecibidas, setTotalUbicacionesRecibidas] = useState(0); // Operadora vacía
  const [totalCarrerasDisponibles, setTotalCarrerasDisponibles] = useState(0); // Estado "Disponible"
  const [totalCarrerasCanceladas, setTotalCarrerasCanceladas] = useState(0); // Estado "Cancelado"
  const [totalTelefonosConvencionales, setTotalTelefonosConvencionales] = useState(0); // 7 dígitos
  const [cargando, setCargando] = useState(false);
  const [cargandoTodas, setCargandoTodas] = useState(false);
  const [error, setError] = useState('');
  const [vistaActual, setVistaActual] = useState('individual'); // 'individual' o 'todas'

  // Estados para el modal de viajes
  const [modalViajes, setModalViajes] = useState({ visible: false, unidad: '', conductor: '', viajes: [] });
  const [cargandoViajes, setCargandoViajes] = useState(false);

  // Fechas "DD-MM-YYYY" entre dos Date (inclusive), que son los ids de los documentos de fecha
  const fechasEnRango = (inicio, fin) => {
    const fechas = [];
    const actual = new Date(inicio.getFullYear(), inicio.getMonth(), inicio.getDate());
    const finDia = new Date(fin.getFullYear(), fin.getMonth(), fin.getDate());
    while (actual <= finDia) {
      fechas.push(`${String(actual.getDate()).padStart(2, '0')}-${String(actual.getMonth() + 1).padStart(2, '0')}-${actual.getFullYear()}`);
      actual.setDate(actual.getDate() + 1);
    }
    return fechas;
  };

  // Lee las dos estructuras por fecha: todosLosViajes/{fecha}/viajes (antigua) y viajes/{fecha}/viajes (actual).
  // Omite los ids ya presentes en idsProcesados y lo va actualizando, para no duplicar con la consulta a la raíz.
  const leerSubcoleccionesPorFecha = async (inicio, fin, idsProcesados) => {
    const encontrados = [];
    for (const fechaString of fechasEnRango(inicio, fin)) {
      for (const raiz of ['todosLosViajes', 'viajes']) {
        try {
          // eslint-disable-next-line no-await-in-loop
          const snap = await getDocs(collection(db, raiz, fechaString, 'viajes'));
          snap.forEach((docSnap) => {
            if (idsProcesados.has(docSnap.id)) return;
            idsProcesados.add(docSnap.id);
            encontrados.push({ id: docSnap.id, data: docSnap.data() });
          });
        } catch (e) {
          console.warn(`⚠️ No se pudo leer ${raiz}/${fechaString}/viajes`, e);
        }
      }
    }
    return encontrados;
  };

  // Fecha del viaje, tolerante a Timestamp, Date o string
  const fechaDelViaje = (viajeData) => {
    if (!viajeData?.fecha) return null;
    const f = viajeData.fecha.toDate ? viajeData.fecha.toDate() : new Date(viajeData.fecha);
    return isNaN(f.getTime()) ? null : f;
  };

  // Obtener lista de unidades desde todosLosViajes y las subcolecciones por fecha
  const obtenerUnidades = async () => {
    try {
      const unidadesMap = new Map(); // unidad -> { numero, nombre, conductor }

      const doceMesesAtras = new Date();
      doceMesesAtras.setFullYear(doceMesesAtras.getFullYear() - 1);

      const registrarUnidad = (d) => {
        const numUnidad = d.unidad || d.numeroUnidad;
        if (!numUnidad) return;
        const u = numUnidad.toString();
        const conductor = d.nombreConductor || d.conductor || 'Sin conductor';
        if (!unidadesMap.has(u)) {
          unidadesMap.set(u, {
            id: u,
            numero: u,
            nombre: `Unidad ${u}`,
            conductor,
            estado: 'Disponible'
          });
        }
      };

      const viajesRef = collection(db, 'todosLosViajes');
      const q = query(viajesRef, where('fecha', '>=', doceMesesAtras));
      const viajesSnapshot = await getDocs(q);

      viajesSnapshot.forEach((docSnap) => registrarUnidad(docSnap.data()));

      // Completar con las subcolecciones por fecha. Se limita a los últimos 31 días
      // porque recorrer los 12 meses día por día serían cientos de lecturas al abrir la pantalla.
      const hace31Dias = new Date();
      hace31Dias.setDate(hace31Dias.getDate() - 31);
      const extras = await leerSubcoleccionesPorFecha(hace31Dias, new Date(), new Set());
      extras.forEach(({ data }) => registrarUnidad(data));

      const unidadesList = Array.from(unidadesMap.values())
        .sort((a, b) => parseInt(a.numero, 10) - parseInt(b.numero, 10));

      setUnidades(unidadesList);
      console.log('🚗 Unidades obtenidas:', unidadesList.length);
    } catch (error) {
      console.error('Error al obtener unidades:', error);
      setError('Error al cargar las unidades desde la colección de viajes');
    }
  };

  // Generar meses del año
  const generarMeses = () => {
    const meses = [
      { valor: '01', nombre: 'Enero' },
      { valor: '02', nombre: 'Febrero' },
      { valor: '03', nombre: 'Marzo' },
      { valor: '04', nombre: 'Abril' },
      { valor: '05', nombre: 'Mayo' },
      { valor: '06', nombre: 'Junio' },
      { valor: '07', nombre: 'Julio' },
      { valor: '08', nombre: 'Agosto' },
      { valor: '09', nombre: 'Septiembre' },
      { valor: '10', nombre: 'Octubre' },
      { valor: '11', nombre: 'Noviembre' },
      { valor: '12', nombre: 'Diciembre' }
    ];
    return meses;
  };

  // Generar años disponibles (últimos 3 años)
  const generarAños = () => {
    const años = [];
    const añoActual = new Date().getFullYear();
    for (let i = añoActual; i >= añoActual - 2; i--) {
      años.push(i);
    }
    return años;
  };

  // Calcular estadísticas de todas las unidades
  const calcularEstadisticasTodasUnidades = async () => {
    if (!fechaInicio || !fechaFin) {
      setError('Por favor selecciona un rango de fechas');
      return;
    }

    setCargandoTodas(true);
    setError('');

    try {
      const estadisticasPorUnidad = {};
      const viajesProcesados = new Set();
      const operadoraStats = {};
      let automaticosCount = 0;
      let ubicacionesRecibidas = 0;
      let carrerasDisponibles = 0;
      let carrerasCanceladas = 0;
      let carrerasNoAtendidasCount = 0;
      let telefonosConvencionalesCount = 0;

      // Rango de fechas
      const inicio = new Date(fechaInicio + 'T00:00:00');
      const fin = new Date(fechaFin + 'T23:59:59');

      // Acumula los totales de un viaje sobre las variables locales
      const procesarDoc = (viajeData) => {
        const helper = {
          stats: {
            ubicacionesRecibidas,
            carrerasDisponibles,
            carrerasCanceladas,
            carrerasNoAtendidasCount,
            telefonosConvencionalesCount,
            automaticosCount
          },
          operadoraStats
        };

        procesarViajeGeneral(viajeData, estadisticasPorUnidad, helper);

        ubicacionesRecibidas = helper.stats.ubicacionesRecibidas;
        carrerasDisponibles = helper.stats.carrerasDisponibles;
        carrerasCanceladas = helper.stats.carrerasCanceladas;
        carrerasNoAtendidasCount = helper.stats.carrerasNoAtendidasCount;
        telefonosConvencionalesCount = helper.stats.telefonosConvencionalesCount;
        automaticosCount = helper.stats.automaticosCount;
      };

      try {
        const viajesRef = collection(db, 'todosLosViajes');
        const q = query(viajesRef, where('fecha', '>=', inicio), where('fecha', '<=', fin));
        const viajesSnapshot = await getDocs(q);

        viajesSnapshot.forEach((docSnapshot) => {
          if (!viajesProcesados.has(docSnapshot.id)) {
            viajesProcesados.add(docSnapshot.id);
            procesarDoc(docSnapshot.data());
          }
        });

      } catch (error) {
        console.error('Error al consultar todosLosViajes:', error);
      }

      // Subcolecciones por fecha (todosLosViajes/{fecha}/viajes y viajes/{fecha}/viajes)
      try {
        const extras = await leerSubcoleccionesPorFecha(inicio, fin, viajesProcesados);
        extras.forEach(({ data }) => procesarDoc(data));
      } catch (error) {
        console.error('Error al consultar subcolecciones por fecha:', error);
      }

      const estadisticasArray = Object.values(estadisticasPorUnidad)
        .sort((a, b) => parseInt(a.unidad) - parseInt(b.unidad));

      setEstadisticasTodasUnidades(estadisticasArray);
      setTotalCarrerasMes(viajesProcesados.size);
      setTotalCarrerasNoAtendidos(carrerasNoAtendidasCount);
      setTotalUbicacionesRecibidas(ubicacionesRecibidas);
      setTotalCarrerasDisponibles(carrerasDisponibles);
      setTotalCarrerasCanceladas(carrerasCanceladas);
      setTotalTelefonosConvencionales(telefonosConvencionalesCount);
      setTotalViajesAutomaticos(automaticosCount);
      setResumenPorOperadora(operadoraStats);

    } catch (error) {
      console.error('Error:', error);
      setError('Error al calcular estadísticas');
    } finally {
      setCargandoTodas(false);
    }
  };

  const asegurarUnidadEnEstadisticas = (estadisticasPorUnidad, u, viajeData) => {
    if (!estadisticasPorUnidad[u]) {
      const conductor = viajeData.nombreConductor || viajeData.conductor || 'Sin conductor';
      estadisticasPorUnidad[u] = {
        unidad: u,
        conductor,
        notificadas: 0,
        rechazadas: 0,
        aceptadas: 0,
        totalCarreras: 0
      };
    }
  };

  const procesarViajeGeneral = (viajeData, estadisticasPorUnidad, helper) => {
    const estado = (viajeData.estado || viajeData.pedido || '').toString().toLowerCase();
    const operadora = (viajeData.operadora || viajeData.operador || '').toString();
    const tipoPedido = (viajeData.tipoPedido || viajeData.tipopedido || '').toString().toLowerCase();
    const conductorDoc = viajeData.nombreConductor || viajeData.conductor || 'Sin conductor';

    // Notificaciones y Rechazos (solo desde todosLosViajes)
    if (viajeData.conductoresCercanos && Array.isArray(viajeData.conductoresCercanos)) {
      viajeData.conductoresCercanos.forEach(c => {
        if (c.unidad) {
          const u = c.unidad.toString();
          asegurarUnidadEnEstadisticas(estadisticasPorUnidad, u, { ...viajeData, nombreConductor: conductorDoc });
          estadisticasPorUnidad[u].notificadas++;
          if (c.rechazado === true) {
            estadisticasPorUnidad[u].rechazadas++;
          }
        }
      });
    }

    // Aceptadas
    const unidadAsignada = viajeData.unidad || viajeData.numeroUnidad;
    if (unidadAsignada && (estado.includes('completado') || estado.includes('finalizado') || estado.includes('aceptado'))) {
      const u = unidadAsignada.toString();
      asegurarUnidadEnEstadisticas(estadisticasPorUnidad, u, viajeData);
      estadisticasPorUnidad[u].aceptadas++;
      estadisticasPorUnidad[u].totalCarreras++;
    }

    // Operadora Stats
    let opKey = operadora || 'Sin Operador';
    if (tipoPedido.includes('auto') && !operadora) opKey = 'Automático';
    if (!helper.operadoraStats[opKey]) {
      helper.operadoraStats[opKey] = { nombre: opKey, totalCarreras: 0 };
    }
    helper.operadoraStats[opKey].totalCarreras++;

    // Global Stats
    if (!operadora || operadora === '' || operadora === 'Sin operador') {
      helper.stats.ubicacionesRecibidas++;
    }
    if (estado.includes('disponible')) helper.stats.carrerasDisponibles++;
    if (estado.includes('cancelado')) helper.stats.carrerasCanceladas++;
    if (estado.includes('no atendido')) helper.stats.carrerasNoAtendidasCount++;
    if (tipoPedido.includes('auto')) helper.stats.automaticosCount++;

    const tel = (viajeData.telefono || viajeData.celular || '').toString().replace(/\D/g, '');
    if (tel.length === 7) helper.stats.telefonosConvencionalesCount++;
  };


  // Calcular estadísticas de la unidad
  const calcularEstadisticasUnidad = async () => {
    if (!unidadSeleccionada || !fechaInicio || !fechaFin) {
      setError('Por favor selecciona una unidad y un rango de fechas');
      return;
    }

    setCargando(true);
    setError('');

    try {
      const estadisticasData = {
        unidad: unidadSeleccionada,
        fechaInicio: fechaInicio,
        fechaFin: fechaFin,
        totalCarreras: 0,
        notificadas: 0,
        rechazadas: 0,
        aceptadas: 0,
        carrerasCompletadas: 0,
        carrerasCanceladas: 0,
        totalIngresos: 0,
        calificacionPromedio: 0,
        calificaciones: [],
        viajesPorDia: {},
        conductorAsignado: '',
        kilometrajeTotal: 0,
        tiempoTotalServicio: 0
      };

      // Rango de fechas
      const inicio = new Date(fechaInicio + 'T00:00:00');
      const fin = new Date(fechaFin + 'T23:59:59');

      const claveDia = (fechaViaje) => (fechaViaje || new Date()).toLocaleDateString('es-EC', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      }).replace(/\//g, '-');

      const idsProcesados = new Set();

      try {
        const viajesRef = collection(db, 'todosLosViajes');
        const q = query(viajesRef, where('unidad', '==', unidadSeleccionada), where('fecha', '>=', inicio), where('fecha', '<=', fin));
        const viajesSnapshot = await getDocs(q);

        viajesSnapshot.forEach((docSnapshot) => {
          idsProcesados.add(docSnapshot.id);
          const viajeData = docSnapshot.data();
          procesarViaje(viajeData, estadisticasData, claveDia(fechaDelViaje(viajeData)));
        });
      } catch (error) {
        console.error('Error al consultar todosLosViajes para unidad:', error);
      }

      // Subcolecciones por fecha: se filtra la unidad en cliente
      try {
        const extras = await leerSubcoleccionesPorFecha(inicio, fin, idsProcesados);
        extras.forEach(({ data }) => {
          const u = (data.unidad || data.numeroUnidad || '').toString();
          if (u !== unidadSeleccionada.toString()) return;
          procesarViaje(data, estadisticasData, claveDia(fechaDelViaje(data)));
        });
      } catch (error) {
        console.error('Error al consultar subcolecciones por fecha para unidad:', error);
      }

      // Calcular promedios y estadísticas finales
      if (estadisticasData.calificaciones.length > 0) {
        estadisticasData.calificacionPromedio =
          estadisticasData.calificaciones.reduce((sum, cal) => sum + cal, 0) / estadisticasData.calificaciones.length;
      }

      setEstadisticas(estadisticasData);
      console.log('📊 Estadísticas calculadas:', estadisticasData);

    } catch (error) {
      console.error('Error al calcular estadísticas:', error);
      setError('Error al calcular las estadísticas de la unidad');
    } finally {
      setCargando(false);
    }
  };

  // Procesar un viaje individual
  const procesarViaje = (viajeData, estadisticasData, fecha) => {
    const estado = (viajeData.estado || viajeData.pedido || '').toString().toLowerCase();

    // Notificaciones y Rechazos
    if (viajeData.conductoresCercanos && Array.isArray(viajeData.conductoresCercanos)) {
      viajeData.conductoresCercanos.forEach(c => {
        if (c.unidad && c.unidad.toString() === estadisticasData.unidad.toString()) {
          estadisticasData.notificadas++;
          if (c.rechazado === true) {
            estadisticasData.rechazadas++;
          }
        }
      });
    }

    // Aceptadas
    if (estado.includes('completado') || estado.includes('finalizado') || estado.includes('aceptado')) {
      estadisticasData.aceptadas++;
      estadisticasData.totalCarreras++;
      estadisticasData.carrerasCompletadas++;
    } else if (estado.includes('cancelado')) {
      estadisticasData.carrerasCanceladas++;
    }

    // Sumar ingresos (usar campo 'valor' si existe, sino 'precio')
    const precio = viajeData.valor || viajeData.precio;
    if (precio && !isNaN(parseFloat(precio))) {
      estadisticasData.totalIngresos += parseFloat(precio);
    }

    // Recopilar calificaciones - buscar en múltiples campos posibles
    let calificacion = null;

    // Buscar en diferentes campos posibles de calificación (priorizar 'rating')
    if (viajeData.rating && !isNaN(parseFloat(viajeData.rating))) {
      calificacion = parseFloat(viajeData.rating);
    } else if (viajeData.calificacion && !isNaN(parseFloat(viajeData.calificacion))) {
      calificacion = parseFloat(viajeData.calificacion);
    } else if (viajeData.estrellas && !isNaN(parseFloat(viajeData.estrellas))) {
      calificacion = parseFloat(viajeData.estrellas);
    } else if (viajeData.puntuacion && !isNaN(parseFloat(viajeData.puntuacion))) {
      calificacion = parseFloat(viajeData.puntuacion);
    } else if (viajeData.score && !isNaN(parseFloat(viajeData.score))) {
      calificacion = parseFloat(viajeData.score);
    }

    if (calificacion !== null && calificacion >= 0 && calificacion <= 5) {
      estadisticasData.calificaciones.push(calificacion);
      console.log('⭐ Calificación encontrada:', calificacion, 'para unidad:', viajeData.unidad);
    }

    // Contar viajes por día
    if (!estadisticasData.viajesPorDia[fecha]) {
      estadisticasData.viajesPorDia[fecha] = 0;
    }
    estadisticasData.viajesPorDia[fecha]++;

    // Obtener conductor asignado
    if (viajeData.nombreConductor && !estadisticasData.conductorAsignado) {
      estadisticasData.conductorAsignado = viajeData.nombreConductor;
    }

    // Obtener operadora asignada - buscar en múltiples campos posibles (individual)
    if (!estadisticasData.operadora) {
      // Lista completa de campos donde puede estar la operadora
      const posiblesOperadoras = [
        viajeData.operadora,
        viajeData.operador,
        viajeData.operadoraNombre,
        viajeData.tipoOperadora,
        viajeData.empresa,
        viajeData.tipoEmpresa,
        viajeData.modoOperacion,
        viajeData.atendidoPor,
        viajeData.operator,
        viajeData.operadorNombre,
        viajeData.nombreOperador,
        viajeData.nombreOperadora
      ];

      // Buscar la primera operadora válida
      for (const operadora of posiblesOperadoras) {
        if (operadora && operadora !== 'Efectivo' && operadora !== '' && operadora !== 'Sin operador') {
          estadisticasData.operadora = operadora;
          break;
        }
      }

      // Si no se encontró ninguna, usar 'Automático' como predeterminado
      if (!estadisticasData.operadora) {
        estadisticasData.operadora = 'Automático';
      }
    }

    // Sumar kilometraje (usar campo 'distancia' si existe)
    if (viajeData.distancia && !isNaN(parseFloat(viajeData.distancia))) {
      estadisticasData.kilometrajeTotal += parseFloat(viajeData.distancia);
    } else if (viajeData.kilometraje && !isNaN(parseFloat(viajeData.kilometraje))) {
      estadisticasData.kilometrajeTotal += parseFloat(viajeData.kilometraje);
    }

    // Debug: mostrar todos los campos disponibles para identificar el campo de calificación
    if (viajeData.unidad === unidadSeleccionada || (estadisticasData && estadisticasData.unidad)) {
      console.log('🔍 Campos disponibles en viaje:', Object.keys(viajeData));
      console.log('🔍 Valores de calificación encontrados:', {
        rating: viajeData.rating,
        calificacion: viajeData.calificacion,
        estrellas: viajeData.estrellas,
        puntuacion: viajeData.puntuacion,
        score: viajeData.score
      });
    }

    console.log('📊 Viaje procesado:', {
      unidad: viajeData.unidad,
      estado: estado,
      precio: precio,
      conductor: viajeData.nombreConductor
    });
  };

  // Formatear fecha
  const formatearFecha = (fecha) => {
    if (!fecha) return '';
    const partes = fecha.split('-');
    if (partes.length !== 3) return fecha;
    const [dia, mes, año] = partes;
    return `${dia}/${mes}/${año}`;
  };



  // Función para abrir modal de viajes de una unidad
  const abrirModalViajes = async (unidad, conductor) => {
    setCargandoViajes(true);
    setModalViajes({ visible: true, unidad, conductor, viajes: [] });

    try {
      const viajesEncontrados = [];
      const inicioMes = new Date(añoSeleccionado, parseInt(mesSeleccionado) - 1, 1, 0, 0, 0);
      const finMes = new Date(añoSeleccionado, parseInt(mesSeleccionado), 0, 23, 59, 59);

      const aFilaViaje = (id, viajeData) => ({
        id,
        fecha: fechaDelViaje(viajeData),
        nombreCliente: viajeData.nombreCliente || viajeData.nombre || 'Sin nombre',
        direccion: viajeData.direccion || viajeData.sector || 'Sin dirección',
        numeroUnidad: viajeData.unidad || viajeData.numeroUnidad || unidad,
        operadora: viajeData.operadora || 'Automático',
        rating: viajeData.rating || null,
        comentario: viajeData.comentario || viajeData.observaciones || 'Sin comentarios',
        estado: viajeData.estado || viajeData.pedido || 'Sin estado'
      });

      const idsProcesados = new Set();

      try {
        // Consultar directamente todosLosViajes con filtros
        const viajesRef = collection(db, 'todosLosViajes');
        const q = query(viajesRef, where('unidad', '==', unidad), where('fecha', '>=', inicioMes), where('fecha', '<=', finMes));
        const viajesSnapshot = await getDocs(q);

        viajesSnapshot.forEach((docSnapshot) => {
          idsProcesados.add(docSnapshot.id);
          viajesEncontrados.push(aFilaViaje(docSnapshot.id, docSnapshot.data()));
        });
      } catch (error) {
        console.error('Error al consultar todosLosViajes para modal:', error);
      }

      // Subcolecciones por fecha: se filtra la unidad en cliente
      try {
        const extras = await leerSubcoleccionesPorFecha(inicioMes, finMes, idsProcesados);
        extras.forEach(({ id, data }) => {
          const u = (data.unidad || data.numeroUnidad || '').toString();
          if (u !== unidad.toString()) return;
          viajesEncontrados.push(aFilaViaje(id, data));
        });
      } catch (error) {
        console.error('Error al consultar subcolecciones por fecha para modal:', error);
      }

      // Ordenar viajes por fecha (más recientes primero)
      viajesEncontrados.sort((a, b) => {
        if (!a.fecha && !b.fecha) return 0;
        if (!a.fecha) return 1;
        if (!b.fecha) return -1;
        return b.fecha - a.fecha;
      });

      setModalViajes(prev => ({ ...prev, viajes: viajesEncontrados }));
      console.log(`📋 Viajes encontrados para unidad ${unidad}:`, viajesEncontrados);

    } catch (error) {
      console.error('Error al cargar viajes de la unidad:', error);
      setError('Error al cargar los viajes de la unidad');
    } finally {
      setCargandoViajes(false);
    }
  };

  // Función para cerrar modal
  const cerrarModalViajes = () => {
    setModalViajes({ visible: false, unidad: '', conductor: '', viajes: [] });
  };

  // Formatear fecha para mostrar en el modal
  const formatearFechaModal = (fecha) => {
    if (!fecha) return 'Sin fecha';
    return fecha.toLocaleString('es-EC', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'short'
    });
  };

  // Función para generar PDF
  const generarPDF = () => {
    if (estadisticasTodasUnidades.length === 0) {
      setError('No hay datos para generar el PDF');
      return;
    }

    const doc = new jsPDF();

    // Configuración del documento
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    // Título del reporte
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('REPORTE DE UNIDADES', pageWidth / 2, 20, { align: 'center' });

    // Subtítulo con mes y año
    doc.setFontSize(14);
    doc.setFont('helvetica', 'normal');
    const mesNombre = generarMeses().find(m => m.valor === mesSeleccionado)?.nombre;
    doc.text(`${mesNombre} ${añoSeleccionado}`, pageWidth / 2, 30, { align: 'center' });

    // Fecha de generación
    doc.setFontSize(10);
    doc.text(`Periodo: ${fechaInicio} al ${fechaFin}`, pageWidth / 2, 40, { align: 'center' });

    // Resumen en la parte superior (en columnas horizontales)
    let currentY = 55;
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('RESUMEN', 20, currentY);

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const totalCarreras = totalCarrerasMes || estadisticasTodasUnidades.reduce((sum, u) => sum + u.totalCarreras, 0);
    const unidadesActivas = estadisticasTodasUnidades.filter(u => u.totalCarreras > 0).length;

    // Configurar columnas (2 columnas)
    const columnWidth = (pageWidth - 40) / 2; // Ancho de cada columna
    let startX = 20; // Posición X inicial
    let currentX = startX;
    let rowY = currentY + 12;

    // Primera fila
    doc.text(`Total de Carreras: ${totalCarreras}`, currentX, rowY);
    currentX = startX + columnWidth;
    doc.text(`Unidades Activas: ${unidadesActivas}`, currentX, rowY);

    // Agregar totales de nuevas métricas si existen
    if (totalUbicacionesRecibidas > 0 || totalCarrerasDisponibles > 0 || totalCarrerasCanceladas > 0) {
      currentX = startX;
      rowY += 10;
      doc.text(`Ubicaciones Recibidas: ${totalUbicacionesRecibidas}`, currentX, rowY);
      currentX = startX + columnWidth;
      doc.text(`Por App (Disponible): ${totalCarrerasDisponibles}`, currentX, rowY);

      currentX = startX;
      rowY += 10;
      doc.text(`Teléfonos Convencionales: ${totalTelefonosConvencionales}`, currentX, rowY);
      currentX = startX + columnWidth;
      doc.setTextColor(239, 68, 68); // Rojo
      doc.text(`Carreras Canceladas: ${totalCarrerasCanceladas}`, currentX, rowY);
      doc.setTextColor(0, 0, 0); // Negro
    }

    // Agregar totales de "No Atendidos" y "Sin Operador" si existen (en columnas)
    if (totalCarrerasNoAtendidos > 0 || totalCarrerasSinOperador > 0) {
      currentX = startX;
      rowY += 10;

      if (totalCarrerasNoAtendidos > 0) {
        doc.setTextColor(239, 68, 68); // Rojo
        doc.text(`Total No Atendidos: ${totalCarrerasNoAtendidos}`, currentX, rowY);
        doc.setTextColor(0, 0, 0); // Volver a negro
        currentX = startX + columnWidth;
      }

      if (totalCarrerasSinOperador > 0) {
        doc.setTextColor(245, 158, 11); // Naranja
        doc.text(`Total Sin Operador: ${totalCarrerasSinOperador}`, currentX, rowY);
        doc.setTextColor(0, 0, 0); // Volver a negro
      }
    }

    // Actualizar currentY para la siguiente sección
    currentY = rowY + 10;

    // Resumen por operadora en columnas horizontales
    if (Object.keys(resumenPorOperadora).length > 0) {
      currentY += 15;
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('CARRERAS POR OPERADORA', 20, currentY);

      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      currentY += 12;

      // Ordenar operadoras por total de carreras
      const operadorasOrdenadas = Object.values(resumenPorOperadora)
        .sort((a, b) => b.totalCarreras - a.totalCarreras);

      // Configurar columnas (2 columnas)
      const columnWidth = (pageWidth - 40) / 2; // Ancho de cada columna (margen izquierdo y derecho: 20 cada uno)
      let startX = 20; // Posición X inicial
      let currentX = startX;
      let rowY = currentY;

      operadorasOrdenadas.forEach((operadora, index) => {
        const nombreOperadora = operadora.nombre;
        doc.text(`${nombreOperadora}: ${operadora.totalCarreras} carreras`, currentX, rowY);

        // Si es el segundo elemento de la fila, pasar a la siguiente fila
        if ((index + 1) % 2 === 0) {
          currentX = startX;
          rowY += 10;
        } else {
          // Mover a la segunda columna
          currentX = startX + columnWidth;
        }
      });

      // Actualizar currentY para la siguiente sección
      currentY = rowY + (operadorasOrdenadas.length % 2 === 0 ? 0 : 10);
    }

    // Preparar datos para la tabla
    const tableData = estadisticasTodasUnidades.map((unidad, index) => [
      index + 1,
      unidad.unidad,
      unidad.conductor || 'Sin conductor',
      unidad.notificadas || 0,
      unidad.rechazadas || 0,
      unidad.aceptadas || 0
    ]);

    // Configuración de la tabla
    autoTable(doc, {
      startY: currentY + 20,
      head: [['#', 'UNIDAD', 'CONDUCTOR', 'NOTIFICADAS', 'RECHAZADAS', 'ACEPTADAS']],
      body: tableData,
      theme: 'grid',
      headStyles: {
        fillColor: [59, 130, 246],
        textColor: 255,
        fontStyle: 'bold',
        fontSize: 10
      },
      bodyStyles: {
        fontSize: 9
      },
      alternateRowStyles: {
        fillColor: [249, 250, 251]
      },
      columnStyles: {
        0: { halign: 'center' },
        1: { halign: 'center' },
        2: { halign: 'left' },
        3: { halign: 'center' },
        4: { halign: 'center' },
        5: { halign: 'center' }
      }
    });

    // Pie de página
    doc.setFontSize(8);
    doc.text('Sistema de Gestión de Taxis', pageWidth / 2, pageHeight - 10, { align: 'center' });

    // Descargar el PDF
    const nombreArchivo = `Reporte_Unidades_${fechaInicio}_${fechaFin}.pdf`;
    doc.save(nombreArchivo);

    console.log('📄 PDF generado exitosamente:', nombreArchivo);
  };

  // Función para generar Excel
  const generarExcel = () => {
    if (estadisticasTodasUnidades.length === 0) {
      setError('No hay datos para generar el Excel');
      return;
    }

    // Preparar datos para Excel
    const datosExcel = estadisticasTodasUnidades.map((unidad, index) => ({
      '#': index + 1,
      'UNIDAD': unidad.unidad,
      'CONDUCTOR': unidad.conductor || 'Sin conductor',
      'NOTIFICADAS': unidad.notificadas || 0,
      'RECHAZADAS': unidad.rechazadas || 0,
      'ACEPTADAS': unidad.aceptadas || 0
    }));

    // Crear hoja de trabajo
    const ws = XLSX.utils.json_to_sheet(datosExcel);

    // Configurar ancho de columnas
    ws['!cols'] = [
      { wch: 5 },  // #
      { wch: 10 }, // UNIDAD
      { wch: 25 }, // CONDUCTOR
      { wch: 15 }, // NOTIFICADAS
      { wch: 15 }, // RECHAZADAS
      { wch: 15 }  // ACEPTADAS
    ];

    // Crear libro de trabajo
    const wb = XLSX.utils.book_new();
    const mesNombre = generarMeses().find(m => m.valor === mesSeleccionado)?.nombre;
    XLSX.utils.book_append_sheet(wb, ws, `Reporte ${mesNombre} ${añoSeleccionado}`);

    // Agregar hoja de resumen
    const resumenData = [
      { 'Métrica': 'Total de Carreras', 'Valor': estadisticasTodasUnidades.reduce((sum, u) => sum + u.totalCarreras, 0) },
      { 'Métrica': 'Unidades Activas', 'Valor': estadisticasTodasUnidades.filter(u => u.totalCarreras > 0).length },
      {
        'Métrica': 'Rating Promedio', 'Valor': estadisticasTodasUnidades.length > 0 ?
          (estadisticasTodasUnidades.reduce((sum, u) => sum + u.calificacionPromedio, 0) / estadisticasTodasUnidades.length).toFixed(1) : 0
      },
      { 'Métrica': 'Viajes Automáticos', 'Valor': totalViajesAutomaticos },
      { 'Métrica': 'Fecha de Generación', 'Valor': new Date().toLocaleDateString('es-EC') }
    ];

    const wsResumen = XLSX.utils.json_to_sheet(resumenData);
    wsResumen['!cols'] = [{ wch: 20 }, { wch: 15 }];
    XLSX.utils.book_append_sheet(wb, wsResumen, 'Resumen');

    // Descargar el archivo
    const nombreArchivo = `Reporte_Unidades_${fechaInicio}_${fechaFin}.xlsx`;
    XLSX.writeFile(wb, nombreArchivo);

    console.log('📊 Excel generado exitosamente:', nombreArchivo);
  };

  // Inicializar datos al cargar el componente
  useEffect(() => {
    obtenerUnidades();

    const hoy = new Date();
    const hoyISO = hoy.toISOString().split('T')[0];
    setFechaInicio(hoyISO);
    setFechaFin(hoyISO);

    // Establecer mes actual por defecto
    const mesActual = String(new Date().getMonth() + 1).padStart(2, '0');
    setMesSeleccionado(mesActual);
  }, []);

  return (
    <div style={{ padding: 20 }}>
      <div style={{ marginBottom: 30 }}>
        <h2 style={{
          margin: '0 0 10px 0',
          color: '#1f2937',
          fontSize: '28px',
          fontWeight: 'bold'
        }}>
          🚗 Reporte de Unidades
        </h2>
        <p style={{ color: '#6b7280', margin: '0 0 20px 0' }}>
          Estadísticas detalladas por unidad y mes
        </p>

        {/* Botones de vista */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
          <button
            onClick={() => setVistaActual('individual')}
            style={{
              background: vistaActual === 'individual' ? '#3b82f6' : '#e5e7eb',
              color: vistaActual === 'individual' ? 'white' : '#374151',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            📊 Unidad Individual
          </button>
          <button
            onClick={() => setVistaActual('todas')}
            style={{
              background: vistaActual === 'todas' ? '#3b82f6' : '#e5e7eb',
              color: vistaActual === 'todas' ? 'white' : '#374151',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            📋 Todas las Unidades
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div style={{
        background: '#f9fafb',
        padding: 20,
        borderRadius: 8,
        marginBottom: 20,
        border: '1px solid #e5e7eb'
      }}>
        <h3 style={{ margin: '0 0 15px 0', color: '#374151' }}>Filtros</h3>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 15 }}>
          {/* Selección de Unidad - Solo para vista individual */}
          {vistaActual === 'individual' && (
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#374151' }}>
                Unidad
              </label>
              <select
                value={unidadSeleccionada}
                onChange={(e) => setUnidadSeleccionada(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              >
                <option value="">Seleccionar unidad</option>
                {unidades.map(unidad => (
                  <option key={unidad.id} value={unidad.numero}>
                    {unidad.nombre} ({unidad.numero})
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Filtro por Fecha */}
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#374151' }}>
              Fecha Desde
            </label>
            <input
              type="date"
              value={fechaInicio}
              onChange={(e) => setFechaInicio(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#374151' }}>
              Fecha Hasta
            </label>
            <input
              type="date"
              value={fechaFin}
              onChange={(e) => setFechaFin(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            />
          </div>
        </div>

        <div style={{ marginTop: 15, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {vistaActual === 'individual' ? (
            <button
              onClick={calcularEstadisticasUnidad}
              disabled={cargando || !unidadSeleccionada || !fechaInicio || !fechaFin}
              style={{
                background: cargando || !unidadSeleccionada || !fechaInicio || !fechaFin ? '#9ca3af' : '#3b82f6',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: cargando || !unidadSeleccionada || !fechaInicio || !fechaFin ? 'not-allowed' : 'pointer'
              }}
            >
              {cargando ? '🔄 Calculando...' : '📊 Generar Reporte Individual'}
            </button>
          ) : (
            <>
              <button
                onClick={calcularEstadisticasTodasUnidades}
                disabled={cargandoTodas || !fechaInicio || !fechaFin}
                style={{
                  background: cargandoTodas || !fechaInicio || !fechaFin ? '#9ca3af' : '#10b981',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: cargandoTodas || !fechaInicio || !fechaFin ? 'not-allowed' : 'pointer'
                }}
              >
                {cargandoTodas ? '🔄 Calculando...' : '📋 Generar Reporte de Todas las Unidades'}
              </button>

              <button
                onClick={generarPDF}
                disabled={estadisticasTodasUnidades.length === 0}
                style={{
                  background: estadisticasTodasUnidades.length === 0 ? '#9ca3af' : '#dc2626',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: estadisticasTodasUnidades.length === 0 ? 'not-allowed' : 'pointer'
                }}
              >
                📄 Generar PDF
              </button>

              <button
                onClick={generarExcel}
                disabled={estadisticasTodasUnidades.length === 0}
                style={{
                  background: estadisticasTodasUnidades.length === 0 ? '#9ca3af' : '#059669',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: estadisticasTodasUnidades.length === 0 ? 'not-allowed' : 'pointer'
                }}
              >
                📊 Generar Excel
              </button>
            </>
          )}
        </div>
      </div>

      {/* Mensaje de error */}
      {
        error && (
          <div style={{
            background: '#fef2f2',
            border: '1px solid #fecaca',
            color: '#dc2626',
            padding: 12,
            borderRadius: 6,
            marginBottom: 20
          }}>
            {error}
          </div>
        )
      }

      {/* Estadísticas */}
      {
        Object.keys(estadisticas).length > 0 && (
          <div>
            {/* Resumen Principal */}
            <div style={{
              background: 'white',
              padding: 20,
              borderRadius: 8,
              marginBottom: 20,
              border: '1px solid #e5e7eb',
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
            }}>
              <h3 style={{ margin: '0 0 15px 0', color: '#1f2937', textAlign: 'center' }}>
                📊 Resumen de la Unidad {estadisticas.unidad} - {estadisticas.fechaInicio} / {estadisticas.fechaFin}
              </h3>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 15 }}>
                <div style={{ textAlign: 'center', padding: 15, background: '#f0f9ff', borderRadius: 6 }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1e40af' }}>
                    {estadisticas.notificadas || 0}
                  </div>
                  <div style={{ fontSize: '14px', color: '#1e40af' }}>Notificadas</div>
                </div>

                <div style={{ textAlign: 'center', padding: 15, background: '#fef2f2', borderRadius: 6 }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#dc2626' }}>
                    {estadisticas.rechazadas || 0}
                  </div>
                  <div style={{ fontSize: '14px', color: '#dc2626' }}>Rechazadas</div>
                </div>

                <div style={{ textAlign: 'center', padding: 15, background: '#f0fdf4', borderRadius: 6 }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#166534' }}>
                    {estadisticas.aceptadas || 0}
                  </div>
                  <div style={{ fontSize: '14px', color: '#166534' }}>Aceptadas (Total)</div>
                </div>
              </div>
            </div>

            {/* Detalles Adicionales */}
            <div style={{
              background: 'white',
              padding: 20,
              borderRadius: 8,
              marginBottom: 20,
              border: '1px solid #e5e7eb',
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
            }}>
              <h3 style={{ margin: '0 0 15px 0', color: '#1f2937' }}>Detalles Adicionales</h3>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 15 }}>
                <div>
                  <strong>Conductor Asignado:</strong> {estadisticas.conductorAsignado || 'No asignado'}
                </div>
                <div>
                  <strong>Total Calificaciones:</strong> {estadisticas.calificaciones.length}
                </div>
                <div>
                  <strong>Promedio de Carreras por Día:</strong> {estadisticas.totalCarreras > 0 ? (estadisticas.totalCarreras / 30).toFixed(1) : 0}
                </div>
              </div>
            </div>

            {/* Viajes por Día */}
            {Object.keys(estadisticas.viajesPorDia).length > 0 && (
              <div style={{
                background: 'white',
                padding: 20,
                borderRadius: 8,
                border: '1px solid #e5e7eb',
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
              }}>
                <h3 style={{ margin: '0 0 15px 0', color: '#1f2937' }}>Viajes por Día</h3>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 10 }}>
                  {Object.entries(estadisticas.viajesPorDia)
                    .sort(([a], [b]) => a.localeCompare(b))
                    .map(([fecha, cantidad]) => (
                      <div key={fecha} style={{
                        textAlign: 'center',
                        padding: 10,
                        background: '#f8fafc',
                        borderRadius: 4,
                        border: '1px solid #e2e8f0'
                      }}>
                        <div style={{ fontSize: '12px', color: '#64748b', marginBottom: 4 }}>
                          {formatearFecha(fecha)}
                        </div>
                        <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#1e293b' }}>
                          {cantidad}
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
        )
      }

      {/* Tabla de todas las unidades */}
      {
        vistaActual === 'todas' && estadisticasTodasUnidades.length > 0 && (
          <div style={{
            background: 'white',
            padding: 20,
            borderRadius: 8,
            border: '1px solid #e5e7eb',
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
            marginTop: 20
          }}>
            <h3 style={{ margin: '0 0 20px 0', color: '#1f2937' }}>
              📋 Reporte de Todas las Unidades - {fechaInicio} / {fechaFin}
            </h3>

            <div style={{ overflowX: 'auto' }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '14px'
              }}>
                <thead>
                  <tr style={{ background: '#f8fafc' }}>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #e5e7eb', fontWeight: '600' }}>
                      #
                    </th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #e5e7eb', fontWeight: '600' }}>
                      UNIDAD
                    </th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #e5e7eb', fontWeight: '600' }}>
                      CONDUCTOR
                    </th>
                    <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #e5e7eb', fontWeight: '600' }}>
                      NOTIFICADAS
                    </th>
                    <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #e5e7eb', fontWeight: '600' }}>
                      RECHAZADAS
                    </th>
                    <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #e5e7eb', fontWeight: '600' }}>
                      ACEPTADAS
                    </th>
                    <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #e5e7eb', fontWeight: '600' }}>
                      ACCIONES
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {estadisticasTodasUnidades.map((unidad, index) => (
                    <tr key={unidad.unidad} style={{
                      borderBottom: '1px solid #e5e7eb',
                      background: index % 2 === 0 ? 'white' : '#f9fafb'
                    }}>
                      <td style={{ padding: '12px', fontWeight: '500', color: '#6b7280' }}>
                        {index + 1}
                      </td>
                      <td style={{ padding: '12px', fontWeight: '600', color: '#1f2937' }}>
                        {unidad.unidad}
                      </td>
                      <td style={{
                        padding: '12px',
                        color: unidad.conductor === 'No atendidos' ? '#ef4444' : '#374151',
                        fontWeight: unidad.conductor === 'No atendidos' ? 'bold' : 'normal'
                      }}>
                        {unidad.conductor || 'No asignado'}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center', fontWeight: '600', color: '#1f2937' }}>
                        {unidad.notificadas || 0}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center', fontWeight: 'bold', color: '#ef4444' }}>
                        {unidad.rechazadas || 0}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center', color: '#10b981', fontWeight: '600' }}>
                        {unidad.aceptadas || 0}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        <button
                          onClick={() => abrirModalViajes(unidad.unidad, unidad.conductor)}
                          style={{
                            background: '#3b82f6',
                            color: 'white',
                            border: 'none',
                            padding: '6px 12px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontWeight: '500',
                            cursor: 'pointer'
                          }}
                        >
                          📋 Ver Viajes
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

          </div>
        )
      }

      {/* Modal de Viajes */}
      {
        modalViajes.visible && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              background: 'white',
              borderRadius: 8,
              padding: 20,
              maxWidth: '90vw',
              maxHeight: '90vh',
              overflow: 'auto',
              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
            }}>
              {/* Header del Modal */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 20,
                borderBottom: '1px solid #e5e7eb',
                paddingBottom: 15
              }}>
                <h3 style={{ margin: 0, color: '#1f2937' }}>
                  📋 Viajes de la Unidad {modalViajes.unidad} - {modalViajes.conductor}
                </h3>
                <button
                  onClick={cerrarModalViajes}
                  style={{
                    background: '#ef4444',
                    color: 'white',
                    border: 'none',
                    padding: '8px 12px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  ✕ Cerrar
                </button>
              </div>

              {/* Contenido del Modal */}
              {cargandoViajes ? (
                <div style={{ textAlign: 'center', padding: 40 }}>
                  <div style={{ fontSize: '18px', color: '#6b7280' }}>🔄 Cargando viajes...</div>
                </div>
              ) : modalViajes.viajes.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 40 }}>
                  <div style={{ fontSize: '18px', color: '#6b7280' }}>📭 No se encontraron viajes para esta unidad</div>
                </div>
              ) : (
                <div>
                  <div style={{ marginBottom: 15, color: '#6b7280' }}>
                    Total de viajes encontrados: <strong>{modalViajes.viajes.length}</strong>
                  </div>

                  <div style={{ overflowX: 'auto' }}>
                    <table style={{
                      width: '100%',
                      borderCollapse: 'collapse',
                      fontSize: '12px'
                    }}>
                      <thead>
                        <tr style={{ background: '#f8fafc' }}>
                          <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #e5e7eb', fontWeight: '600' }}>
                            FECHA
                          </th>
                          <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #e5e7eb', fontWeight: '600' }}>
                            CLIENTE
                          </th>
                          <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #e5e7eb', fontWeight: '600' }}>
                            DIRECCIÓN
                          </th>
                          <th style={{ padding: '8px', textAlign: 'center', borderBottom: '2px solid #e5e7eb', fontWeight: '600' }}>
                            UNIDAD
                          </th>
                          <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #e5e7eb', fontWeight: '600' }}>
                            OPERADORA
                          </th>
                          <th style={{ padding: '8px', textAlign: 'center', borderBottom: '2px solid #e5e7eb', fontWeight: '600' }}>
                            RATING
                          </th>
                          <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #e5e7eb', fontWeight: '600' }}>
                            COMENTARIO
                          </th>
                          <th style={{ padding: '8px', textAlign: 'center', borderBottom: '2px solid #e5e7eb', fontWeight: '600' }}>
                            ESTADO
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {modalViajes.viajes.map((viaje, index) => (
                          <tr key={viaje.id} style={{
                            borderBottom: '1px solid #e5e7eb',
                            background: index % 2 === 0 ? 'white' : '#f9fafb'
                          }}>
                            <td style={{ padding: '8px', color: '#374151', fontSize: '11px' }}>
                              {formatearFechaModal(viaje.fecha)}
                            </td>
                            <td style={{ padding: '8px', color: '#1f2937', fontWeight: '500' }}>
                              {viaje.nombreCliente}
                            </td>
                            <td style={{ padding: '8px', color: '#374151', maxWidth: '200px', wordWrap: 'break-word' }}>
                              {viaje.direccion}
                            </td>
                            <td style={{ padding: '8px', textAlign: 'center', color: '#1f2937', fontWeight: '600' }}>
                              {viaje.numeroUnidad}
                            </td>
                            <td style={{ padding: '8px', color: '#374151' }}>
                              {viaje.operadora}
                            </td>
                            <td style={{ padding: '8px', textAlign: 'center', color: '#7c3aed', fontWeight: '600' }}>
                              {viaje.rating ? `${viaje.rating} ⭐` : 'N/A'}
                            </td>
                            <td style={{ padding: '8px', color: '#374151', maxWidth: '150px', wordWrap: 'break-word' }}>
                              {viaje.comentario}
                            </td>
                            <td style={{ padding: '8px', textAlign: 'center' }}>
                              <span style={{
                                padding: '2px 6px',
                                borderRadius: '4px',
                                fontSize: '10px',
                                fontWeight: '500',
                                background: viaje.estado.toLowerCase().includes('completado') || viaje.estado.toLowerCase().includes('aceptado') ? '#dcfce7' :
                                  viaje.estado.toLowerCase().includes('cancelado') ? '#fef2f2' : '#f3f4f6',
                                color: viaje.estado.toLowerCase().includes('completado') || viaje.estado.toLowerCase().includes('aceptado') ? '#166534' :
                                  viaje.estado.toLowerCase().includes('cancelado') ? '#dc2626' : '#374151'
                              }}>
                                {viaje.estado}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        )
      }
    </div>
  );
}

export default ReporteUnidades;
